import {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  useRef,
  RefObject,
  useCallback,
} from 'react';
import { uniq } from 'lodash';

import { Breakpoint, useScreenSize } from '@/lib/hooks/use-screen-size';
import { useLayoutContext } from '@/components/layout/layout-context';
import { getCell } from '@/core/cell';

import { useBuildAccountUrl } from '@/lib/accounts/context';

import { buildExplorationUrl } from '../utils';

import { useDirtyContext } from '../dirty-context';
import {
  Cell,
  DeriveFieldOperation,
  Exploration,
  ExplorationParameters,
  FilterOperation,
  GroupAggregateOperation,
  RelationAggregateOperation,
} from '../types';
import { useConversationRepositoryContext } from '../conversation/conversation-repository-context';

export type FormId =
  | 'addFilter'
  | 'addGroupAggregate'
  | 'addSwitchToRelation'
  | 'addRelationAggregate'
  | 'addDeriveField';

type ConversationState = {
  [key: string]:
    | {
        draft: string | null;
        progress: Record<string, string | null>;
      }
    | undefined;
};

type UIState = {
  selectedCellId: string | null;
  scrollToId: string | null;
  highlightedCellId: string | null;
  addFormData:
    | {
        index: number;
        formId: 'addFilter';
        parameters?: FilterOperation['parameters'];
      }
    | {
        index: number;
        formId: 'addGroupAggregate';
        parameters?: GroupAggregateOperation['parameters'];
      }
    | {
        index: number;
        formId: 'addRelationAggregate';
        parameters?: RelationAggregateOperation['parameters'];
      }
    | {
        index: number;
        formId: 'addDeriveField';
        parameters?: DeriveFieldOperation['parameters'];
      }
    | {
        index: number;
        formId?: 'addSwitchToRelation';
      }
    | null;
  editFormIndex: number | null;
  newCellIndex: number | null;
  newConversationSubjectId: string | null;
  conversations: ConversationState;
  openVisualisationSections: number[];
};

type OpenEditorAction = {
  type: 'OPEN_EDITOR';
  payload: { cellId: string };
};

type CloseEditorAction = {
  type: 'CLOSE_EDITOR';
};

type SelectCellAction = {
  type: 'SELECT_CELL';
  payload: { cellId: string | null };
};

type DeselectCellAction = {
  type: 'DESELECT_CELL';
};

type ScrollToCellAction = {
  type: 'SCROLL_TO_CELL';
  payload: { cellId: string | null };
};

type HighlightCellAction = {
  type: 'HIGHLIGHT_CELL';
  payload: { cellId: string | null };
};

type OpenAddMenuAction = {
  type: 'OPEN_ADD_MENU';
  payload: { index: number };
};

type OpenAddFormAction = {
  type: 'OPEN_ADD_FORM';
  payload:
    | { index: number; formId: 'addFilter'; parameters: FilterOperation['parameters'] }
    | {
        index: number;
        formId: 'addGroupAggregate';
        parameters: GroupAggregateOperation['parameters'];
      }
    | {
        index: number;
        formId: 'addRelationAggregate';
        parameters: RelationAggregateOperation['parameters'];
      }
    | { index: number; formId: 'addDeriveField'; parameters: DeriveFieldOperation['parameters'] }
    | { index: number; formId: FormId };
};

type CloseAddFormAction = {
  type: 'CLOSE_ADD_FORM';
};

type OpenEditFormAction = {
  type: 'OPEN_EDIT_FORM';
  payload: { index: number };
};

type CloseEditFormAction = {
  type: 'CLOSE_EDIT_FORM';
};

type SetNewCellIndex = {
  type: 'SET_NEW_CELL_INDEX';
  payload: { index: number | null };
};

type SetNewConversationIndex = {
  type: 'SET_NEW_CONVERSATION_SUBJECT_ID';
  payload: { cellId: string | null };
};

type RemoveNewConversationIndex = {
  type: 'REMOVE_NEW_CONVERSATION_INDEX';
};

type SetConversationDraft = {
  type: 'SET_CONVERSATION_DRAFT';
  payload: { conversationId: string; draft: string | null };
};

type SetConversationProgress = {
  type: 'SET_CONVERSATION_PROGRESS';
  payload: { conversationId: string; message: string | null };
};

type SetOpenVisualisationSections = {
  type: 'SET_OPEN_VISUALISATION_SECTIONS';
  payload: { sections: number[] };
};

type UIAction =
  | OpenEditorAction
  | CloseEditorAction
  | SelectCellAction
  | DeselectCellAction
  | ScrollToCellAction
  | HighlightCellAction
  | OpenAddMenuAction
  | OpenAddFormAction
  | CloseAddFormAction
  | OpenEditFormAction
  | CloseEditFormAction
  | SetNewCellIndex
  | SetNewConversationIndex
  | RemoveNewConversationIndex
  | SetConversationDraft
  | SetConversationProgress
  | SetOpenVisualisationSections;

const getInitialState = (exploration: Exploration, screenSize: Breakpoint): UIState => ({
  selectedCellId:
    exploration.view.cells.length === 1 &&
    screenSize > Breakpoint.md &&
    !['conversation', 'chat'].includes(exploration.view.cells.at(0)?.kind ?? '')
      ? (exploration.view.cells.at(0)?.id ?? null)
      : null,
  scrollToId: null,
  highlightedCellId: null,
  addFormData: null,
  editFormIndex: null,
  newCellIndex: null,
  newConversationSubjectId: null,
  conversations: {},
  openVisualisationSections: [],
});

const reducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case 'OPEN_EDITOR':
      return {
        ...state,
        selectedCellId: action.payload?.cellId ?? state.selectedCellId,
        scrollToId: action.payload?.cellId === state.scrollToId ? state.scrollToId : null,
        addFormData: action.payload?.cellId !== state.selectedCellId ? null : state.addFormData,
        editFormIndex: action.payload?.cellId !== state.selectedCellId ? null : state.editFormIndex,
      };
    case 'CLOSE_EDITOR':
      return {
        ...state,
        scrollToId: null,
        selectedCellId: null,
        addFormData: null,
      };
    case 'SELECT_CELL':
      return {
        ...state,
        selectedCellId: action.payload.cellId,
        addFormData: null,
        editFormIndex: null,
      };
    case 'DESELECT_CELL':
      return {
        ...state,
        selectedCellId: null,
      };
    case 'OPEN_ADD_MENU':
    case 'OPEN_ADD_FORM':
      return {
        ...state,
        addFormData: action.payload,
        editFormIndex: null,
      };
    case 'CLOSE_ADD_FORM':
      return {
        ...state,
        addFormData: null,
      };
    case 'OPEN_EDIT_FORM':
      return {
        ...state,
        editFormIndex: action.payload.index,
        addFormData: null,
      };
    case 'CLOSE_EDIT_FORM':
      return {
        ...state,
        editFormIndex: null,
      };
    case 'SCROLL_TO_CELL':
      return {
        ...state,
        scrollToId: action.payload.cellId,
      };
    case 'HIGHLIGHT_CELL':
      return {
        ...state,
        highlightedCellId: action.payload.cellId,
      };
    case 'SET_NEW_CELL_INDEX':
      return {
        ...state,
        newCellIndex: action.payload.index,
      };
    case 'SET_NEW_CONVERSATION_SUBJECT_ID':
      return {
        ...state,
        newConversationSubjectId: action.payload.cellId,
      };
    case 'REMOVE_NEW_CONVERSATION_INDEX':
      return {
        ...state,
        newConversationSubjectId: null,
      };
    case 'SET_CONVERSATION_DRAFT':
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [action.payload.conversationId]: {
            progress: {
              ...state.conversations[action.payload.conversationId]?.progress,
            },
            draft: action.payload.draft,
          },
        },
      };
    case 'SET_CONVERSATION_PROGRESS':
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [action.payload.conversationId]: {
            draft: state.conversations[action.payload.conversationId]?.draft ?? null,
            progress: {
              general: action.payload.message,
            },
          },
        },
      };
    case 'SET_OPEN_VISUALISATION_SECTIONS':
      return {
        ...state,
        openVisualisationSections: action.payload.sections,
      };
    default:
      return state;
  }
};

export interface ExplorationUIContextValue {
  // UI state values
  selectedCellId: string | null;
  scrollToId: string | null;
  highlightedCellId: string | null;
  addFormData: UIState['addFormData'];
  editFormIndex: number | null;
  newCellIndex: number | null;
  newConversationSubjectId: string | null;
  conversations: ConversationState;
  openVisualisationSections: number[];
  conversationAbortCallbacks: RefObject<Map<string, () => void>>;

  // UI state methods
  openEditor: (cellId: string) => void;
  closeEditor: () => void;
  selectCell: (cellId: string) => void;
  deselectCell: () => void;
  openAddMenu: (payload: OpenAddMenuAction['payload']) => void;
  openAddForm: (payload: OpenAddFormAction['payload']) => void;
  closeAddForm: () => void;
  openEditForm: (payload: OpenEditFormAction['payload']) => void;
  closeEditForm: () => void;
  scrollToCell: (cellId: string | null) => void;
  highlightCell: (cellId: string | null) => void;
  setNewCellIndex: (index: number | null) => void;
  setNewConversationSubjectId: (cellId: string | null) => void;
  setConversationDraft: (conversationId: string, draft: string | null) => void;
  setConversationProgress: (conversationId: string, message: string | null) => void;
  setOpenVisualisationSections: (sections: number[]) => void;
  openVisualisationEditor: (visualisationIdx: number) => void;
  getExplorationLink: (params?: Record<string, string>) => URL;

  // Computed values
  isEditorOpen: boolean;
  selectedCell: Cell | null;
  isDirty: boolean;
}

const defaultContextValue = Symbol();

const ExplorationUIContext = createContext<ExplorationUIContextValue | typeof defaultContextValue>(
  defaultContextValue,
);

export const useExplorationUIContext = () => {
  const context = useContext(ExplorationUIContext);
  if (context === defaultContextValue) {
    throw new Error('useUIStateContext must be used within a UIStateProvider');
  }
  return context;
};

interface ExplorationUIContextProviderProps {
  children: ReactNode;
  exploration: Exploration;
  parameters: ExplorationParameters;
}

export const ExplorationUIContextProvider = (props: ExplorationUIContextProviderProps) => {
  const { children, exploration, parameters } = props;
  const screenSize = useScreenSize();
  const [state, dispatch] = useReducer(
    reducer,
    getInitialState(exploration, screenSize.breakpoint),
  );
  const { confirmUnsavedChangesIfNeeded } = useDirtyContext();
  const { toggleRightSidebar } = useLayoutContext();
  const buildAccountUrl = useBuildAccountUrl();
  const conversationAbortCallbacks = useRef(new Map<string, () => void>());

  const isDirty =
    exploration.options?.explorationSourceId !== undefined &&
    exploration.options?.explorationSourceId !== exploration.explorationId;

  const openEditor = useCallback(
    (cellId: string) => {
      confirmUnsavedChangesIfNeeded({
        onConfirm: () => {
          toggleRightSidebar(true);
          dispatch({ type: 'OPEN_EDITOR', payload: { cellId } });
        },
      });
    },
    [confirmUnsavedChangesIfNeeded, toggleRightSidebar],
  );

  const closeEditor = useCallback(() => {
    confirmUnsavedChangesIfNeeded({
      onConfirm: () => {
        dispatch({ type: 'CLOSE_EDITOR' });
      },
    });
  }, [confirmUnsavedChangesIfNeeded]);

  const openVisualisationEditor = useCallback(
    (idx: number) => {
      dispatch({
        type: 'SET_OPEN_VISUALISATION_SECTIONS',
        payload: { sections: uniq([...state.openVisualisationSections, idx]) },
      });
    },
    [state.openVisualisationSections],
  );

  const selectCell = useCallback((cellId: string) => {
    dispatch({ type: 'SELECT_CELL', payload: { cellId } });
  }, []);

  const deselectCell = useCallback(() => {
    dispatch({ type: 'DESELECT_CELL' });
  }, []);

  const openAddMenu = useCallback(
    (payload: OpenAddMenuAction['payload']) => {
      confirmUnsavedChangesIfNeeded({
        onConfirm: () => {
          dispatch({ type: 'OPEN_ADD_MENU', payload });
        },
      });
    },
    [confirmUnsavedChangesIfNeeded],
  );

  const openAddForm = useCallback((payload: OpenAddFormAction['payload']) => {
    dispatch({ type: 'OPEN_ADD_FORM', payload });
  }, []);

  const closeAddForm = useCallback(() => {
    dispatch({ type: 'CLOSE_ADD_FORM' });
  }, []);

  const openEditForm = useCallback(
    (payload: OpenEditFormAction['payload']) => {
      confirmUnsavedChangesIfNeeded({
        onConfirm: () => {
          dispatch({ type: 'OPEN_EDIT_FORM', payload });
        },
      });
    },
    [confirmUnsavedChangesIfNeeded],
  );

  const closeEditForm = useCallback(() => {
    dispatch({ type: 'CLOSE_EDIT_FORM' });
  }, []);

  const scrollToCell = useCallback((cellId: string | null) => {
    dispatch({ type: 'SCROLL_TO_CELL', payload: { cellId } });
  }, []);

  const highlightCell = useCallback((cellId: string | null) => {
    dispatch({ type: 'HIGHLIGHT_CELL', payload: { cellId } });

    // Automatically clear the highlight after 2.5 seconds
    if (cellId !== null) {
      setTimeout(() => {
        dispatch({ type: 'HIGHLIGHT_CELL', payload: { cellId: null } });
      }, 2500);
    }
  }, []);

  const setNewCellIndex = useCallback((index: number | null) => {
    dispatch({ type: 'SET_NEW_CELL_INDEX', payload: { index } });
  }, []);

  const setNewConversationSubjectId = useCallback((cellId: string | null) => {
    dispatch({ type: 'SET_NEW_CONVERSATION_SUBJECT_ID', payload: { cellId } });
  }, []);

  const setConversationDraft = useCallback((conversationId: string, draft: string | null) => {
    dispatch({ type: 'SET_CONVERSATION_DRAFT', payload: { conversationId, draft } });
  }, []);

  const setConversationProgress = useCallback((conversationId: string, message: string | null) => {
    dispatch({ type: 'SET_CONVERSATION_PROGRESS', payload: { conversationId, message } });
  }, []);

  const setOpenVisualisationSections = useCallback((sections: number[]) => {
    dispatch({ type: 'SET_OPEN_VISUALISATION_SECTIONS', payload: { sections } });
  }, []);

  const getExplorationLink = useCallback(
    (params: Record<string, string> = {}) => {
      const explorationUrl = buildAccountUrl(buildExplorationUrl(exploration, parameters, params));
      return new URL(explorationUrl, location.origin);
    },
    [buildAccountUrl, exploration, parameters],
  );

  const { getExplorationCell: getCellFromConversation } = useConversationRepositoryContext();

  const selectedCell =
    state.selectedCellId !== null
      ? (getCell(exploration.view.cells, state.selectedCellId) ??
        getCellFromConversation(state.selectedCellId) ??
        null)
      : null;

  return (
    <ExplorationUIContext.Provider
      value={{
        selectedCellId: state.selectedCellId,
        scrollToId: state.scrollToId,
        highlightedCellId: state.highlightedCellId,
        addFormData: state.addFormData,
        editFormIndex: state.editFormIndex,
        newCellIndex: state.newCellIndex,
        newConversationSubjectId: state.newConversationSubjectId,
        conversations: state.conversations,
        openVisualisationSections: state.openVisualisationSections,
        conversationAbortCallbacks,

        openEditor,
        closeEditor,
        selectCell,
        deselectCell,
        openAddMenu,
        openAddForm,
        closeAddForm,
        openEditForm,
        closeEditForm,
        scrollToCell,
        highlightCell,
        setNewCellIndex,
        setNewConversationSubjectId,
        setConversationDraft,
        setConversationProgress,
        setOpenVisualisationSections,
        openVisualisationEditor,
        getExplorationLink,

        isEditorOpen: selectedCell !== null,
        selectedCell,
        isDirty,
      }}>
      {children}
    </ExplorationUIContext.Provider>
  );
};
